import { useEffect, useRef, useCallback } from 'react'
import { studentService } from '../../../_state/services/student.service'
import { API_ROOT } from '../../../_state/helpers/api-config'

const TICK_INTERVAL_MS = 30_000
const INACTIVITY_THRESHOLD_MS = 20 * 60 * 1000

function getAuthToken() {
    return localStorage.getItem('authToken')
}

function sendEndSessionSync() {
    const token = getAuthToken()
    if (!token) return

    try {
        const xhr = new XMLHttpRequest()
        xhr.open('GET', `${API_ROOT}user/v2/session/end?deviceType=Web&appVersion=4.0.0`, false)
        xhr.setRequestHeader('Authorization', `Bearer ${token}`)
        xhr.send()
    } catch {
        // Best-effort -- if it fails the hourly Hangfire job will close the session
    }
}

export default function useSessionHeartbeat() {
    const hiddenAtRef = useRef(null)
    const intervalRef = useRef(null)

    const tick = useCallback(() => {
        studentService.tickSession().catch(() => {})
    }, [])

    const startTicking = useCallback(() => {
        clearInterval(intervalRef.current)
        intervalRef.current = setInterval(tick, TICK_INTERVAL_MS)
    }, [tick])

    const stopTicking = useCallback(() => {
        clearInterval(intervalRef.current)
        intervalRef.current = null
    }, [])

    const onVisibilityChange = useCallback(() => {
        if (document.hidden) {
            stopTicking()
            hiddenAtRef.current = Date.now()
        } else {
            const hiddenDuration = hiddenAtRef.current
                ? Date.now() - hiddenAtRef.current
                : 0
            hiddenAtRef.current = null

            if (hiddenDuration >= INACTIVITY_THRESHOLD_MS) {
                studentService.startSession().catch(() => {})
            } else {
                tick()
            }
            startTicking()
        }
    }, [tick, startTicking, stopTicking])

    const onBeforeUnload = useCallback(() => {
        stopTicking()
        sendEndSessionSync()
    }, [stopTicking])

    useEffect(() => {
        studentService.startSession().catch(() => {})
        startTicking()

        document.addEventListener('visibilitychange', onVisibilityChange)
        window.addEventListener('beforeunload', onBeforeUnload)

        return () => {
            stopTicking()
            document.removeEventListener('visibilitychange', onVisibilityChange)
            window.removeEventListener('beforeunload', onBeforeUnload)
            studentService.endSession().catch(() => {})
        }
    }, [startTicking, stopTicking, onVisibilityChange, onBeforeUnload])
}
